import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import moment from 'moment';
import { BILL_ALLOCATION_STATE } from 'src/api/billAutomation/bills';
import { resolveApPushInvoiceNumber } from 'src/pages/NewBillingModule/ApplyBillingFull/AppfolioPush';
import { isHeldByAppfolioPush } from './appfolioPushRow';
import { monthKeyOfDate } from './monthKeys';
// The bill details' own date helpers, so the table and the details show one date the same way (LB-5983).
import { formatServicePeriod, formatShortDate } from './BillDetails/billDetailsUtils';
import { getUtilityKinds, OTHER_UTILITY_ICON, UTILITY_ICON_BY_KIND } from './BillDetails/resolveAllocationTargets';
export const DASH = '—';

/** Where a bill entered the system, keyed by the service's BillSource values. */
export const BILL_SOURCES = {
  api: 'API',
  billsDashboard: 'Bills Dashboard',
  email: 'Email',
  integration: 'Integration',
  propertyAllocation: 'Property Allocation',
  scrape: 'Web automation'
};
const BILL_SOURCE_BY_API_VALUE = {
  Api: BILL_SOURCES.api,
  Email: BILL_SOURCES.email,
  Integration: BILL_SOURCES.integration,
  Manual: BILL_SOURCES.propertyAllocation,
  Portal: BILL_SOURCES.billsDashboard,
  Scrape: BILL_SOURCES.scrape
};

/**
 * Table labels for the service's BillStatus values, in pipeline order.
 *
 * The words are the PRD's rather than the service's: a bill that has been through validation is
 * "Processed" to the person reading the table, and one the pipeline could not place needs review.
 * Only the labels differ — every comparison goes through these keys, so the API's own values are
 * untouched.
 */
export const BILL_STATUSES = {
  pending: 'Pending',
  processing: 'Processing',
  extracted: 'Extracting',
  validated: 'Processed',
  needsProperty: 'Needs review',
  /**
   * Arrived at the company's bill-intake address from a sender we don't recognise. Deliberately not
   * called "Error": nothing went wrong, we declined it — and the PM can undo that by approving the
   * sender. "Rejected" is the word that invites that action.
   */
  rejected: 'Rejected',
  /** The same document the company already sent. Nothing to do; it's here so they can see we got it. */
  duplicate: 'Duplicate',
  failed: 'Error'
};
const BILL_STATUS_BY_API_VALUE = {
  Pending: BILL_STATUSES.pending,
  Processing: BILL_STATUSES.processing,
  Extracted: BILL_STATUSES.extracted,
  Validated: BILL_STATUSES.validated,
  NeedsProperty: BILL_STATUSES.needsProperty,
  Rejected: BILL_STATUSES.rejected,
  Duplicate: BILL_STATUSES.duplicate,
  Failed: BILL_STATUSES.failed
};

/**
 * The name each icon is filtered and labelled by: one for every icon this feature can draw — each of
 * the resolver's kinds, the combined water/sewer icon getUtilityIconForName returns, and the "other"
 * icon for a service it names no utility for. An icon drawn here without a name here reads as
 * "Other" in the filter, which is the same drift this change removes.
 */
const UTILITY_LABEL_BY_ICON = {
  cable: 'Cable/Satellite',
  electricity: 'Electricity',
  internet: 'Internet',
  landscaping: 'Landscaping',
  meter: 'Other',
  'natural-gas': 'Gas',
  sewer: 'Sewer',
  trash: 'Trash',
  water: 'Water',
  'water/sewer': 'Water/Sewer'
};

/**
 * The utilities column is icon-only, so the filter labels are derived from those icons. Going
 * through the icon keeps a bill's own wording for a service out of the filter's vocabulary.
 */
export function getUtilityLabel(iconType) {
  return UTILITY_LABEL_BY_ICON[iconType] || 'Other';
}

/**
 * The icons for the utilities a bill charges for. The bill names its services in its own words
 * ("electric", "Refuse Collection"), so they are read for the utility they name — the same reading
 * the allocation suggestion matches a property's utilities with. One utility billed twice, on two
 * properties or as two line groups, is one icon.
 *
 * A service covering two utilities is drawn as both, unlike getUtilityIconForName, which answers with
 * a single icon because it labels one tab. This column is a list of the utilities on the bill, so a
 * "Water/Sewer" service belongs in it twice — once under water and once under sewer.
 */
export const getBillUtilityIcons = utilities => [...new Set((utilities || []).flatMap(utility => {
  const kinds = getUtilityKinds(utility === null || utility === void 0 ? void 0 : utility.serviceType);
  return kinds.length ? kinds.map(kind => UTILITY_ICON_BY_KIND[kind] || OTHER_UTILITY_ICON) : [OTHER_UTILITY_ICON];
}))];

/**
 * The states in which a service already has an allocation bill behind it — approved, held for
 * review, or still being calculated. All three live in the allocation service: a bill with any of them
 * can no longer be edited, and deleting or retrying it archives the allocation with it (LB-5952).
 */
const ALLOCATED_STATES = [BILL_ALLOCATION_STATE.allocating, BILL_ALLOCATION_STATE.awaitingApproval, BILL_ALLOCATION_STATE.needsReview, BILL_ALLOCATION_STATE.allocated, BILL_ALLOCATION_STATE.skipped];

/** The services of a bill that have been allocated. A service refused outright is not one of them. */
export const getAllocatedServices = utilities => (utilities || []).filter(utility => ALLOCATED_STATES.includes(utility === null || utility === void 0 ? void 0 : utility.allocationState));

/**
 * How many allocations a delete or a retry archives with the bill. The service's count is the whole of it: every live
 * allocation made from the bill, including one made in Apply Billing on a property the bill's services do not list.
 * From a service too old to send it, the allocated services stand in (one per allocation; services listed twice under
 * one allocation count once), or the column's count when no services are listed.
 */
const countListedAllocations = utilities => new Set(getAllocatedServices(utilities).map((utility, index) => utility.allocationBillId || "service-".concat(index))).size;
export const countAllocations = (utilities, allocation, allocationCount) => {
  var _allocation$done;
  // `done` counts services, not allocations: two services on one combined utility share one. Only a bill with no
  // services listed has nothing better.
  const unlisted = utilities !== null && utilities !== void 0 && utilities.length ? 0 : (_allocation$done = allocation === null || allocation === void 0 ? void 0 : allocation.done) !== null && _allocation$done !== void 0 ? _allocation$done : 0;
  return Math.max(allocationCount !== null && allocationCount !== void 0 ? allocationCount : 0, countListedAllocations(utilities), unlisted);
};

/** The allocation column's word for a service still short of allocated, by BILL_ALLOCATION_STATE. */
const OUTSTANDING_ALLOCATION = {
  [BILL_ALLOCATION_STATE.notAllocated]: {
    label: 'Not allocated',
    variant: 'inactive'
  },
  [BILL_ALLOCATION_STATE.failed]: {
    label: 'Failed',
    variant: 'error'
  },
  [BILL_ALLOCATION_STATE.allocating]: {
    label: 'Allocating',
    variant: 'info'
  },
  [BILL_ALLOCATION_STATE.awaitingApproval]: {
    label: 'Awaiting approval',
    variant: 'warning'
  },
  // Held for a comparison before it can be approved: still awaiting approval. Not "Needs review", the status
  // column's word for a bill with no property.
  [BILL_ALLOCATION_STATE.needsReview]: {
    label: 'Awaiting approval',
    variant: 'warning'
  },
  [BILL_ALLOCATION_STATE.skipped]: {
    label: 'Skipped',
    variant: 'inactive'
  }
};

/** A state this page does not know yet: said as pending, not claimed to be "Not allocated". */
const UNKNOWN_ALLOCATION = {
  label: 'Pending',
  variant: 'inactive'
};

/**
 * The month a service is scheduled for (LB-6009), "Oct 2026", or null: only while it waits for that month, not allocated
 * nor on its way. With the company's month in progress known, a month that has opened is no longer waited for, and a
 * service Allocate was refused for before it opened reads as waiting rather than failed. Without it, a service not
 * allocated says the month it is for, and a failed one stays failed.
 *
 * @param monthInProgress the company's month in progress as a month key (see useMonthInProgress), or null
 */
export const getScheduledMonth = function (utility) {
  let monthInProgress = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
  const scheduled = monthKeyOfDate(utility === null || utility === void 0 ? void 0 : utility.scheduledFor);
  if (scheduled === null) return null;
  const state = utility === null || utility === void 0 ? void 0 : utility.allocationState;
  const isWaiting = monthInProgress === null ? state === BILL_ALLOCATION_STATE.notAllocated : scheduled > monthInProgress && [BILL_ALLOCATION_STATE.notAllocated, BILL_ALLOCATION_STATE.failed].includes(state);
  return isWaiting ? moment(utility.scheduledFor, 'YYYY-MM-DD').format('MMM YYYY') : null;
};

/** The allocation column's word for a service still to allocate: its scheduled month, else its state's. */
const outstandingChipFor = monthInProgress => utility => {
  const month = getScheduledMonth(utility, monthInProgress);
  if (month) return {
    label: "Scheduled for ".concat(month),
    variant: 'info',
    title: utility.scheduleReason || undefined
  };
  return OUTSTANDING_ALLOCATION[utility === null || utility === void 0 ? void 0 : utility.allocationState] || UNKNOWN_ALLOCATION;
};

/** "Scheduled for Oct 2026" inside a sentence: only its first letter lowered, so the month keeps its capital. */
const inSentence = label => label.charAt(0).toLowerCase() + label.slice(1);

/** Which tone a mix of states takes: the most pressing among them. */
const VARIANT_URGENCY = ['error', 'warning', 'info', 'inactive'];

/**
 * The allocation column's tag: what is left to do, in words, rather than "1/1 Pending", which read the same for a
 * table waiting on its approval as for a bill nobody had allocated. One state across the outstanding services names
 * it ("Awaiting approval", or "1/2 awaiting approval" beside an allocated one); a mix keeps the count and names
 * each state on hover. A bill with no services listed yet has only the service's count to go on.
 */
export const getAllocationChipProps = function (utilities, allocation) {
  let allocationCount = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 0;
  let monthInProgress = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : null;
  const services = utilities || [];
  if (!services.length) {
    var _allocation$total, _allocation$done2;
    const total = (_allocation$total = allocation === null || allocation === void 0 ? void 0 : allocation.total) !== null && _allocation$total !== void 0 ? _allocation$total : 0;
    const pending = Math.max(total - ((_allocation$done2 = allocation === null || allocation === void 0 ? void 0 : allocation.done) !== null && _allocation$done2 !== void 0 ? _allocation$done2 : 0), 0);
    if (!total) return {
      label: DASH,
      variant: 'inactive'
    };
    return pending ? {
      label: "".concat(pending, "/").concat(total, " Pending"),
      variant: 'inactive'
    } : {
      label: 'Allocated',
      variant: 'success'
    };
  }
  const outstanding = services.filter(utility => (utility === null || utility === void 0 ? void 0 : utility.allocationState) !== BILL_ALLOCATION_STATE.allocated).map(outstandingChipFor(monthInProgress));

  // Allocated in Apply Billing on a property none of the listed services is on: the services cannot show it, but a
  // delete archives it and an edit is refused for it, so the column says it too.
  const elsewhere = Math.max((allocationCount !== null && allocationCount !== void 0 ? allocationCount : 0) - countListedAllocations(services), 0);
  const elsewhereNote = elsewhere ? "".concat(elsewhere, " more allocated on a property this bill does not list") : null;
  const withNote = chip => elsewhereNote ? _objectSpread(_objectSpread({}, chip), {}, {
    title: [chip.title, elsewhereNote].filter(Boolean).join('; ')
  }) : chip;
  if (!outstanding.length) return withNote({
    label: 'Allocated',
    variant: 'success'
  });
  const counts = outstanding.reduce((byLabel, _ref) => {
    let label = _ref.label;
    return _objectSpread(_objectSpread({}, byLabel), {}, {
      [label]: (byLabel[label] || 0) + 1
    });
  }, {});
  const labels = Object.keys(counts);
  if (labels.length > 1) {
    return withNote({
      label: "".concat(outstanding.length, "/").concat(services.length, " Pending"),
      variant: VARIANT_URGENCY.find(variant => outstanding.some(state => state.variant === variant)) || 'inactive',
      title: labels.map(label => "".concat(counts[label], " ").concat(inSentence(label))).join(', ')
    });
  }
  const _outstanding = _slicedToArray(outstanding, 1),
    _outstanding$ = _outstanding[0],
    label = _outstanding$.label,
    variant = _outstanding$.variant,
    title = _outstanding$.title;
  if (outstanding.length === services.length) {
    if (elsewhere && label === OUTSTANDING_ALLOCATION[BILL_ALLOCATION_STATE.notAllocated].label) {
      return {
        label: 'Allocated elsewhere',
        variant: 'info',
        title: elsewhereNote
      };
    }
    return withNote(_objectSpread({
      label,
      variant
    }, title ? {
      title
    } : {}));
  }
  return withNote({
    label: "".concat(outstanding.length, "/").concat(services.length, " ").concat(inSentence(label)),
    variant
  });
};

/**
 * Why a bill cannot be deleted, or null when it can. The two holds are unrelated and a bill can be
 * under both, so they are named separately rather than collapsed into one apology — being told a bill
 * was allocated when what is actually holding it is a payable in AppFolio sends someone looking in the
 * wrong place.
 */
/**
 * Why the bill's details can no longer be edited. Same vocabulary as the delete guard on purpose — a
 * bill held by an allocation and one held by a payable are different problems with different fixes, and
 * naming the wrong one sends somebody looking in the wrong place.
 *
 * `isPushed` covers a push still in flight: `isHeldByAppfolioPush` counts `pending`, and an edit made
 * between the trigger and the answer is discarded without trace — the request already carried the
 * pre-edit data, and this side's edit is session-only.
 */
export const getEditBlockedReason = _ref2 => {
  let isAllocated = _ref2.isAllocated,
    isPushed = _ref2.isPushed;
  if (isAllocated && isPushed) {
    return 'This bill has been allocated and pushed to AppFolio, so its details can no longer be edited.';
  }
  if (isPushed) return 'This bill has been pushed to AppFolio, so its details can no longer be edited.';
  if (isAllocated) return 'This bill has been allocated, so its details can no longer be edited.';
  return null;
};

/**
 * Whether any service of the bill holds it against a delete or a retry (LB-5952): billed to residents, or allocated in
 * a billing month that has closed. Allocated alone does not -- deleting or retrying the bill is how a wrong one is
 * corrected, and its allocation is archived with it.
 */
export const getBillingHold = function (utilities) {
  let bill = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return {
    // The row's own word covers every allocation made from the bill, as the service's guard does; a service's covers its own.
    isBilled: !!(bill !== null && bill !== void 0 && bill.isBilled) || (utilities || []).some(utility => utility === null || utility === void 0 ? void 0 : utility.isBilled),
    isClosed: !!(bill !== null && bill !== void 0 && bill.isClosed) || (utilities || []).some(utility => utility === null || utility === void 0 ? void 0 : utility.isClosed)
  };
};

/**
 * Why a selection's held bills stay, for any mix of them: each reason that applies, said for bills in general rather
 * than for the one a single reason would describe.
 */
export const getSelectionDeleteHint = heldRows => {
  const reasons = [heldRows.some(row => row.isPushed) && 'pushed to AppFolio', heldRows.some(row => row.isBilled) && 'billed to residents', heldRows.some(row => row.isClosed) && 'allocated in a billing month that has closed'].filter(Boolean);
  return reasons.length ? "Bills ".concat(reasons.join(' or '), " can no longer be deleted.") : null;
};

/** The one sentence a billing hold gives, in the words of the action refused; null when nothing holds. */
const billingHoldReason = (_ref3, verb) => {
  let isBilled = _ref3.isBilled,
    isClosed = _ref3.isClosed;
  if (isBilled) return "This bill has been billed to residents, so it can no longer be ".concat(verb, ".");
  if (isClosed) return "This bill was allocated in a billing month that has closed, so it can no longer be ".concat(verb, ".");
  return null;
};
export const getDeleteBlockedReason = _ref4 => {
  let _ref4$isBilled = _ref4.isBilled,
    isBilled = _ref4$isBilled === void 0 ? false : _ref4$isBilled,
    _ref4$isClosed = _ref4.isClosed,
    isClosed = _ref4$isClosed === void 0 ? false : _ref4$isClosed,
    isPushed = _ref4.isPushed;
  if (isPushed) return 'This bill has been pushed to AppFolio and can no longer be deleted.';
  return billingHoldReason({
    isBilled,
    isClosed
  }, 'deleted');
};

/** The service's statuses a finished read can be in — the only ones a retry is offered for (LB-5787). */
const RETRYABLE_EXTRACT_STATUSES = ['Validated', 'NeedsProperty', 'Failed'];

/**
 * Why a bill cannot be retried, or null when it can. The same holds as for delete, since a retry
 * archives the bill; on top of that, only a finished read is worth retrying. The Retry dialog asks the
 * service for the final word (including whether the file itself can be read again).
 */
export const getRetryBlockedReason = _ref5 => {
  let _ref5$isBilled = _ref5.isBilled,
    isBilled = _ref5$isBilled === void 0 ? false : _ref5$isBilled,
    _ref5$isClosed = _ref5.isClosed,
    isClosed = _ref5$isClosed === void 0 ? false : _ref5$isClosed,
    isPushed = _ref5.isPushed,
    extractStatus = _ref5.extractStatus;
  if (isPushed) return 'This bill has been pushed to AppFolio and can no longer be retried.';
  const held = billingHoldReason({
    isBilled,
    isClosed
  }, 'retried');
  if (held) return held;
  if (!RETRYABLE_EXTRACT_STATUSES.includes(extractStatus)) {
    return extractStatus === 'Rejected' || extractStatus === 'Duplicate' ? 'Only a bill that was read can be retried.' : 'This bill is still being read.';
  }
  return null;
};

/**
 * Both ends of the period are billed, so 1–31 March is 31 days and not 30. Times of day are dropped
 * first, otherwise a period stamped at different hours loses or gains a day.
 */
export const getServiceDayCount = (start, end) => {
  if (!(start !== null && start !== void 0 && start.isValid()) || !(end !== null && end !== void 0 && end.isValid())) return DASH;
  return Math.max(end.clone().startOf('day').diff(start.clone().startOf('day'), 'days') + 1, 1);
};

/**
 * How long ago the bill arrived. The column is about recency rather than a date — "2 hours ago"
 * answers "is this new?" at a glance, which the timestamp does not. The exact time is kept on the
 * cell's tooltip for when it matters.
 */
export const getReceivedLabel = acquiredAt => {
  const at = acquiredAt ? moment(acquiredAt) : null;
  return at !== null && at !== void 0 && at.isValid() ? at.fromNow() : DASH;
};

/** An id rather than a person is no use in a table cell. */
const isGuid = value => /^[0-9a-f]{8}(-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(value || '');

/**
 * Who the bill came from: the sender's address when it arrived by email, otherwise the channel it
 * came through and, when the service names one, the person who put it there.
 */
export const getFromLabel = bill => {
  if (bill.senderEmail) return bill.senderEmail;
  const source = BILL_SOURCE_BY_API_VALUE[bill.source] || bill.source || '';
  const by = isGuid(bill.acquiredBy) ? '' : bill.acquiredBy;
  return [source, by].filter(Boolean).join(' · ') || DASH;
};

/** The address each property is filed under, for reading a bill's property ids back as addresses. */
export const getPropertyAddressIndex = properties => Object.fromEntries((properties || []).map(property => [property.propertyId, property.propertyAddress || property.propertyName || '']));

/**
 * Maps a bill from the company bills endpoint onto the shape the table renders. `addressById` names
 * the company's properties, so a bill matched to one is shown under the address it is filed under
 * rather than the address its provider printed — the two are rarely written the same way.
 */
/**
 * A scraped bill is held for Livable staff until its read account matches the login it came from. Only staff
 * are ever sent a held bill, so this badge is the staff view of why a customer cannot see it yet.
 */
export const HELD_CHIPS = {
  AwaitingAccountCheck: {
    label: 'Staff only: checking account',
    variant: 'info',
    title: 'Customers see this bill once the account it shows matches the account its login is mapped to.'
  },
  AccountMismatch: {
    label: 'Staff only: account mismatch',
    variant: 'error',
    title: 'The account on this bill is not the account its login is mapped to. Customers cannot see it.'
  },
  AuditMismatch: {
    label: 'Staff only: failed audit',
    variant: 'error',
    title: 'The daily check found this bill no longer matches its login mapping. Customers cannot see it.'
  }
};

/**
 * The one hold customers see (LB-6231): a bill emailed to a Pro company after its grace period, held unread as Rejected
 * until the company has Plus or Autopilot, and then read automatically. Not a staff chip: its row says it in the pill.
 */
export const HELD_FOR_PLAN = 'AwaitingTier';
export const HELD_FOR_PLAN_LABEL = 'Held: needs Plus';
/** Why a bill held for the plan can't be attached: bill-automation refuses it, and the upgrade reads it anyway. */
export const HELD_FOR_PLAN_ATTACH_HINT = "It's read automatically once your company has Plus or Autopilot.";
export const mapCompanyBillToRow = function (bill, addressById) {
  var _bill$batchId, _bill$allocation$done, _bill$allocation, _bill$allocation$tota, _bill$allocation2, _bill$note;
  let monthInProgress = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : null;
  let _ref6 = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {},
    _ref6$isBillingOption = _ref6.isBillingOptionsEnabled,
    isBillingOptionsEnabled = _ref6$isBillingOption === void 0 ? false : _ref6$isBillingOption;
  const serviceStart = bill.serviceStartDate ? moment(bill.serviceStartDate) : null;
  const serviceEnd = bill.serviceEndDate ? moment(bill.serviceEndDate) : null;
  const utilityIcons = getBillUtilityIcons(bill.utilities);
  const propertyIds = bill.propertyIds || [];
  const filedAddresses = propertyIds.map(propertyId => addressById === null || addressById === void 0 ? void 0 : addressById[propertyId]).filter(Boolean);
  const allocationCount = countAllocations(bill.utilities, bill.allocation, bill.allocationCount);
  const isAllocated = allocationCount > 0;
  const isPushed = isHeldByAppfolioPush(bill.apPush);
  const _getBillingHold = getBillingHold(bill.utilities, bill),
    isBilled = _getBillingHold.isBilled,
    isClosed = _getBillingHold.isClosed;
  const status = BILL_STATUS_BY_API_VALUE[bill.extractStatus] || bill.extractStatus || BILL_STATUSES.pending;
  return {
    id: bill.billId,
    /** The acquisition event this bill arrived in (LB-5828), so the list can scope to one email/upload. */
    batchId: (_bill$batchId = bill.batchId) !== null && _bill$batchId !== void 0 ? _bill$batchId : null,
    /**
     * A bill is the dashboard's to delete until a service of it is billed to residents, or allocated in a billing month
     * that has closed, or an AppFolio payable exists for it (LB-5952). Allocated alone it can still go, and its
     * allocation is archived with it. The service decides; this only saves offering an action it would turn down.
     */
    canDelete: !isPushed && !isBilled && !isClosed,
    /** Carried on the row so every surface offering the delete says the same thing about it. */
    isAllocated,
    /** How many allocations a delete or a retry archives with the bill, so its dialog says them in the right number. */
    allocationCount,
    isBilled,
    isClosed,
    isPushed,
    deleteBlockedReason: getDeleteBlockedReason({
      isBilled,
      isClosed,
      isPushed
    }),
    retryBlockedReason: getRetryBlockedReason({
      isBilled,
      isClosed,
      isPushed,
      extractStatus: bill.extractStatus
    }),
    fileName: bill.fileName || DASH,
    status,
    /**
     * The service's own word for where extraction got to. The AP push gates on it directly rather
     * than on the mapped label, so the check reads the same value the service does.
     */
    extractStatus: bill.extractStatus || '',
    /**
     * Why the bill is in this status, when the service says. The fallback is whatever label the
     * pill shows — the mapped one when the status is known, the raw API value when it is not — so
     * the hover can never contradict the pill: "Validated" over a pill that says "Processed" reads
     * as two different states.
     */
    statusTooltip: bill.statusReason || status,
    /**
     * Why a scraped bill is held for staff; null for every bill a customer can see. A failed read never reaches the
     * account check, so "checking account" would never end on it (LB-6095); the check runs once it is entered by hand.
     */
    heldChip: bill.heldReason === 'AwaitingAccountCheck' && bill.extractStatus === 'Failed' ? null : HELD_CHIPS[bill.heldReason] || null,
    /**
     * Emailed after a Pro company's grace period and held, unread, for the plan (LB-6233). Its `status` stays Rejected,
     * which the status filter and the tabs ask the server by; only its pill and its actions differ. Only with billing
     * options on: with them off, bill-automation reads a held bill at its next poll, and until then it shows as before.
     */
    heldForPlan: isBillingOptionsEnabled && bill.heldReason === HELD_FOR_PLAN,
    /** The bill a duplicate repeats, so the row can point at what we already hold. */
    duplicateOfBillId: bill.duplicateOfBillId || null,
    billSource: BILL_SOURCE_BY_API_VALUE[bill.source] || bill.source || DASH,
    allocation: {
      done: (_bill$allocation$done = (_bill$allocation = bill.allocation) === null || _bill$allocation === void 0 ? void 0 : _bill$allocation.done) !== null && _bill$allocation$done !== void 0 ? _bill$allocation$done : 0,
      total: (_bill$allocation$tota = (_bill$allocation2 = bill.allocation) === null || _bill$allocation2 === void 0 ? void 0 : _bill$allocation2.total) !== null && _bill$allocation$tota !== void 0 ? _bill$allocation$tota : 0
    },
    /** The allocation column's tag: what is left to do on the bill's services, in words. */
    allocationChip: getAllocationChipProps(bill.utilities, bill.allocation, bill.allocationCount, monthInProgress),
    /**
     * Where the document's AP push stands, read from the bill-automation ledger with the row — so the
     * table needs no status call of its own, and the list refresh is what settles a pending push.
     * Carries no attempts: the details view reads those for the one bill it shows.
     */
    apPush: bill.apPush || null,
    utilityIcons,
    utilityTypes: utilityIcons.map(getUtilityLabel),
    /** What the bill charges for, with the utility each was matched to, for the details view. */
    utilities: bill.utilities || [],
    /** Every property the bill is for, as the company knows them. */
    propertyIds,
    // A bill nobody has matched to a property yet has only what its provider printed to show.
    propertiesLines: filedAddresses.length ? filedAddresses : bill.properties || [],
    account: bill.accountNumber || DASH,
    provider: bill.provider || DASH,
    servicePeriod: formatServicePeriod(bill.serviceStartDate, bill.serviceEndDate),
    /** Raw, for the Bill ready column to ask by (LB-6032). */
    serviceEndDate: bill.serviceEndDate || '',
    /** The date printed on the bill, raw: the Bill ready tooltip leads with it (LB-6032). */
    invoiceDate: bill.invoiceDate || '',
    days: getServiceDayCount(serviceStart, serviceEnd),
    amount: typeof bill.amount === 'number' ? bill.amount : null,
    invoiceNumber: bill.invoiceNumber || DASH,
    /**
     * Whether this row's own numbers give the push an identity to file under. The list carries no parse
     * confidence and no per-property account numbers, so this is the bill-level reading — enough to keep
     * the table from offering a push the service is certain to refuse. The details view, which has the
     * whole extraction, judges the rest.
     */
    pushInvoiceNumber: resolveApPushInvoiceNumber({
      extraction: {
        accountNumber: bill.accountNumber,
        invoiceNumber: bill.invoiceNumber
      },
      typedInvoiceNumber: ''
    }),
    dueDate: formatShortDate(bill.dueDate),
    dateUploaded: formatShortDate(bill.acquiredAt),
    /** Raw, for the received-within filter and the exact time behind the relative label. */
    receivedAt: bill.acquiredAt || '',
    received: getReceivedLabel(bill.acquiredAt),
    from: getFromLabel(bill),
    /** Kept apart from `from` so the cell can tell an address from a channel and link it. */
    senderEmail: bill.senderEmail || '',
    /** A free email provider (LB-5971): the attach dialog offers the exact address, never the whole domain. */
    senderDomainIsFreeMail: !!bill.senderDomainIsFreeMail,
    /** Accepted as a company user or Livable staff (LB-5970): the attach dialog offers no allowed-sender entry. */
    senderAlwaysAccepted: !!bill.senderAlwaysAccepted,
    /** What the customer wrote to our team with the bill (LB-6056), as plain text; empty when there is none. */
    note: ((_bill$note = bill.note) === null || _bill$note === void 0 ? void 0 : _bill$note.trim()) || '',
    noteBy: bill.noteBy || '',
    noteAt: bill.noteAt || ''
  };
};